// web/src/screens/Support.tsx — ask a question; threaded replies (D06 A14; questions ≠ pipeline).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SupportThread } from '../lib/types';
import { age } from '../lib/status';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { Modal } from '../components/Modal';
import { FormField } from '../components/FormField';
import { Icon } from '../components/Icon';
import { toast } from '../components/Toast';

export function Support() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['threads'], queryFn: () => api.listThreads() });
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function ask() {
    if (!subject.trim() || !body.trim()) return;
    setBusy(true); await api.postQuestion(subject.trim(), body.trim()); setBusy(false);
    setOpen(false); setSubject(''); setBody('');
    toast({ kind: 'success', message: 'Question sent' });
    qc.invalidateQueries({ queryKey: ['threads'] });
  }

  const askBtn = (
    <button onClick={() => setOpen(true)}
      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 font-medium text-runink transition-transform active:scale-[0.98]">
      <Icon name="plus" size={16} />Ask a question
    </button>
  );

  return (
    <>
      <PageHeader title="Support" subtitle="Ask a question — this goes straight to us, not the pipeline." action={askBtn} />
      <AsyncBoundary query={q} skeleton={<Skeleton variant="row" count={3} />}
        isEmpty={(t) => t.length === 0}
        empty={{ icon: 'headset', title: 'No questions yet', body: 'Ask us anything about your sites or account.', cta: { label: 'Ask a question', onClick: () => setOpen(true) } }}>
        {(threads) => <div className="flex flex-col gap-3">{threads.map((t) => <ThreadCard key={t.id} thread={t} />)}</div>}
      </AsyncBoundary>

      <Modal open={open} onClose={() => setOpen(false)} title="Ask a question">
        <div className="flex flex-col gap-4">
          <FormField label="Subject" name="q-subject" required value={subject} onChange={setSubject} />
          <FormField label="Your question" name="q-body" required textarea rows={5} value={body} onChange={setBody} />
          <button onClick={ask} disabled={busy || !subject.trim() || !body.trim()}
            className="min-h-11 rounded-md bg-run px-4 font-medium text-runink disabled:opacity-50">{busy ? 'Sending…' : 'Send question'}</button>
        </div>
      </Modal>
    </>
  );
}

function ThreadCard({ thread }: { thread: SupportThread }) {
  const [openIt, setOpenIt] = useState(false);
  return (
    <div className="rounded-lg border border-hair bg-surface p-4">
      <button onClick={() => setOpenIt((v) => !v)} className="flex w-full items-center justify-between gap-3 text-left">
        <span className="min-w-0">
          <span className="block truncate font-medium text-ink">{thread.subject}</span>
          <span className="text-xs text-muted">{thread.status} · {age(thread.lastMessageAt)} ago</span>
        </span>
        <Icon name="chevron-right" size={18} className={`shrink-0 text-muted transition-transform ${openIt ? 'rotate-90' : ''}`} />
      </button>
      {openIt && thread.messages && (
        <div className="mt-3 flex flex-col gap-2 border-t border-hair pt-3">
          {thread.messages.map((m) => (
            <div key={m.id} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.authorRole === 'operator' ? 'self-start bg-surface2 text-ink' : 'self-end bg-run/15 text-ink'}`}>
              <span className="mb-0.5 block text-[10px] uppercase text-muted">{m.authorRole === 'operator' ? 'YourProject' : 'You'}</span>
              {m.body}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
